import { ColorModeOptions } from '@gui/constants/ColorModeOptions';
import Grid from '../Geodata/grid.js';

import { lineMultiGradient, selectColorScheme } from '../Gui/store/ColorScheme.js';

class Serializable {
    serialize() {
        try {
            // Replacer function to handle specific cases
            const replacer = (key, value) => {
                // Map LayerStandard (or subclasses) into item.UID
                // if (value instanceof LayerStandard) {
                //     return value.UID;
                // }
                // Convert Set objects into Arrays

                if (value instanceof Set) {
                    // Set obj require custom serialization
                    return [...value];
                }
                return value;
            };

            // Serialize the object with the custom replacer
            return JSON.stringify(this, replacer);
        } catch (error) {
            console.error('Serialization failed for object:', this);
            console.error('Error details:', error);
            return JSON.stringify({ error: 'Serialization failed' });
        }
    }

    deserialize(jsonData, grid) {
        try {
            // Assuming you know which properties are meant to be Sets
            // For example, if 'openPanels' was originally a Set
            if (jsonData.general && jsonData.general.openPanels) {
                jsonData.general.openPanels = new Set(jsonData.general.openPanels);
            }
            if (jsonData.data && jsonData.data.MIXER_ELEMENTS) {
                jsonData.data.MIXER_ELEMENTS = jsonData.data.MIXER_ELEMENTS.map((element) => ({
                    ...element,
                    layer: this.LayerfromJSON(element.layer, grid),
                    frameDraw: null,
                }));
            }

            // Repeat for other Set properties as needed
            Object.assign(this, jsonData);
        } catch (error) {
            console.error('Error during deserialization: ', error);
            // Optionally, return a default state or handle the error appropriately
        }
    }
    LayerfromJSON(json, grid) {
        if (typeof json === 'string') {
            json = JSON.parse(json);
        }
        if (!json.UID) {
            throw new Error('Invalid data: UID is required to deserialize LayerStandard.');
        }
        const layer = grid.getAllLayers().find((layer) => layer.UID === json.UID);
        return layer;
    }
}

export const guiSettings = {
    hoverModeInstance: null,
    FULLSCREEN: false,
};

// The purpose of this class is to store all the data exchanged
// between the gui and the layer status
// In the future this file could represent the "status" file that we can save/load
// In order to keep track of the user progresses
// So, for now, put here only the status of the active layer

export class layerStatus extends Serializable {
    constructor() {
        super();
    }

    data = { ACTIVE_RESULT_ID: 0 };

    designSchemes = {
        OPEN_TAB: 'Single-Build',

        // Single-Building:
        SINGLE_BUILDING: false,

        SB_VOLUME: false,

        SB_VOLUME_SOURCE: 0,
        SB_VOLUME_SHOW_SOURCE: false,
        SB_VOLUME_SHOW_EMTPY: false,
        SB_VOLUME_SHOW_ALL: false,

        SB_VOLUME_STEP: 3,
        SB_VOLUME_STEP_MIN: 0.5,
        SB_VOLUME_STEP_MAX: 6,

        SB_VOLUME_HEIGHT: Grid.gridRes * Grid.gridZ,
        SB_VOLUME_HEIGHT_MIN: 3,
        SB_VOLUME_HEIGHT_MAX: Grid.gridRes * Grid.gridZ,

        SB_VOLUME_EVERY_N: 1,
        SB_VOLUME_EVERY_N_MIN: 1,
        SB_VOLUME_EVERY_N_MAX: 3,

        SB_CONVEX_HULL: false,

        // Multi-Building:
        MULTI_BUILDING: false,

        // Floor-Based:
        FLOOR_BASED: false,
        F_SLABS: false,
        F_STORIES: false,
        F_STORIES_RECTANGULAR: false,
        F_STORIES_DEFINED: false,
        F_STORIES_TRANSPARENT: false,

        // Room-Based (SuperVoxels):
        ROOM_BASED: false,

        // New Design Schemes:
        CAST_SHADOWS: false,
        CAST_KEEPOUTS: false,
        INNER_SP: false,
        VISIBLE: true,
    };

    solarAnalysis = {
        solarParams: {
            DIFFUSE_RADIATION: { ON: false, VALUE: 10, MIN_VALUE: 0, MAX_VALUE: 20 },
            CLOUD_COVERAGE: { ON: false, VALUE: 50, MIN_VALUE: 0, MAX_VALUE: 100 },
            UNIT_OF_MEASURE: 0,
        },
        yearlyDemand: 0,
        yearlySupply: 0,
        buildingPatterns: {
            GROSS_FLOOR_SURFACE: { VALUE: null, MIN_VALUE: 0, MAX_VALUE: 1000000 },
            USE: 'Residential',
            HEATING_PEAKS: { OCCURRENCES: ['Jan', '-'], TYPE: ['Monthly', 'Hourly'], VALUES: ['1.9', '-'] },
            COOLING_PEAKS: { OCCURRENCES: ['Jul', '18'], TYPE: ['Monthly', 'Hourly'], VALUES: ['0.4', '10'] },
            APPLIANCES_PEAKS: { OCCURRENCES: ['-', '7', '18'], TYPE: ['Monthly', 'Hourly', ''], VALUES: ['-', '27.40', '44.75'] },
            TOTAL_PEAKS: { OCCURRENCES: [], TYPE: [], VALUES: [] },
        },
        photovoltaicPotentials: {
            STRATEGY: 'Manual',
            PANEL_EFFICIENCY: { MIN_VALUE: 10, MAX_VALUE: 40, VALUE: 20 },
            SURFACE_COVERAGE: { MIN_VALUE: 0, MAX_VALUE: 100, VALUE: 100 },
            totalEnergySupply: null,
        },
        solarHeatGains: { STRATEGY: 'Manual', G_VALUE: { MIN_VALUE: 0, MAX_VALUE: 100, VALUE: 25 }, transmittedEnergy: null },
    };

    projections = {
        THETA: 0.0,
        SEL_PROJECTION: 0,

        STANDARD_REDUCTION: true,
        HORIZONTAL_REDUCTION: false,
        AVERAGE_REDUCTION: false,
        ORTHOGONAL_REDUCTION: false,
        ORTHOGONAL_VALUE: 0.0,
    };

    element = {
        SEL_VIS_ELEMENT: 4,

        ELEMENT_VECTORIAL: false,
        ELEMENT_SCALAR: false,
        ELEMENT_VECT_SCALE: true,

        ELEMENT_CURRENT_DENSITY: 0,

        ELEMENT_1TO3: false,
        ELEMENT_1EV3: false,

        normalized: true,
        ELEMENT_SHOW_SELECTED: true,

        ELEMENT_SPIN: 1,

        scaleFactor: 1,
        TRANSPARENCY: 0,
    };

    VECTORIAL_SCALAR_MODE = 0;

    filter = {
        FILTER_GRADIENT_STD_MAX: 70, // Never changes
        FILTER_GRADIENT_STD_MIN: 0, // Never changes
        FILTER_GRADIENT_MAX: 70,
        FILTER_GRADIENT_MIN: 0,

        FILTER_INV: false,
        FILTER_STD: false,
        FILTER_GRAY: false,

        FILTER_VALUE: true,
        SORT_MODE: 0, // 0 = no sort , 1 = low sort, 2 = barycentric sort
        FILTER_GLOBAL: true,

        FILTER_BY_VALUE: false,
        FILTER_BY_INDEX: false,
        INDEX_TOLERANCE: 0,

        IS_MAPPED: false,

        ALONE: false,
        NO_BASE: false,

        ACTIVE_SENSORS_NUMBER: 0,
        ACTIVE_SENSORS_PER_FLOOR: 0,

        // Vars to init range sliders:
        gradient: null,
        threshold: {},
        trimCloseToTerrain: {},
        sliceX: {},
        sliceY: {},
        sliceZ: {},

        PROJECT_SKETCH: false,
        BUILDING_DISTANCE: {},

        FILTER_BY_POLYGON: false,
        FILTER_SHOW_POLYGON: true,
        UNOBSTRUCTED: false,
        UNOBSTRUCTED_MODES: { VOXEL_CENTER: 0, VOXEL_CORNERS: 1 },
        UNOBSTRUCTED_MODE_ON: 1,
        UNOBSTRUCTED_SCALED: false,

        RESET: false,
        ACTIVATE_AFTER_RESET: {},

        ELEMENT_ONLY_BUILDABLE: false, // Filter only in buildable areas
    };

    legend = { MAX_VALUE_TEXT: '', MED_VALUE_TEXT: '', MIN_VALUE_TEXT: '', textureGradient: null };

    operator = {
        LINE_LENGTH: 100, // Used to adjust the length of the lines

        // Show rays
        SHOW_LINES: false, // When true display rays
        FREE: true, // Show FREE rays
        INTERSECTED: true, // Show INTERSECTED rays

        // Rays selection:
        RAYS_SP_QUANTITY: 0, // 0 selected sensor Point 1 all sensor points
        RAYS_QUANTITY: 2, // 0 Single ray, 1 fixed N, 2 all
        RAYS_FIXED_QUANTITY: 2,

        // Show intersections
        SHOW_INTERSECTIONS: false,
        CLOSEST: true,
        OTHER: true,

        // Itersections selection
        ALL_INTERSECTIONS: 0,
        ELEMENT_INTERSECTIONS: 1,
        ON_CURRENT_LINES: 2,
        INTERSECTIONS_MODE: 1,

        // Sensor point selection
        ACTIVE_SP_SHOW_RAYS_1TO3: null,
        ACTIVE_SP_INDEX: Grid.firstActiveID,
        ACTIVE_SP_INDEX_POSITION: null,
        X: Grid.indexToXYZ(Grid.firstActiveID)[0],
        Y: Grid.indexToXYZ(Grid.firstActiveID)[1],
        Z: Grid.indexToXYZ(Grid.firstActiveID)[2],

        // Heatmap:
        bar2D: null,
        UPDATE_HEATMAP: true,
        HEATMAP_MODE_FV: false,
    };

    segments = {
        FRAME_MAX: 0,
        FRAME_SELECTED: 0,
        FRAME_NAMES: [],

        IS_LOCAL: false,
        IS_GO: false,
        IS_AGGREGATE: false,
        aggregator: null,
        FRAMES_LIST: [0],
        FRAME_FILTER_POLYGON_POINTS: [], // Array of array.
    };

    metrics = { NOISE_LEVEL: 60, WIND_LEVEL: 2.5 };

    clone() {
        const jsonString = this.serialize();
        const jsonData = JSON.parse(jsonString);

        const clonedObject = new layerStatus(); // Create a new instance

        for (const key in jsonData) {
            if (key != 'data' && key != 'segments' && key != 'legend') {
                clonedObject[key] = jsonData[key];
                console.log(key);
            }
        }

        return clonedObject;
    }
}

export class worldStatusClass extends Serializable {
    constructor() {
        super();
    }

    lineColor = lineMultiGradient;
    SHOW_ERROR_POPUP = true;
    TRIGGER_ERROR_POPUP = true;
    TRIGGER_ERROR_POPUP_MESSAGE = '';
    TRIGGER_ERROR_POPUP_MESSAGE_COLOR = undefined;

    general = {
        openPanels: new Set(['Operator', 'Context', 'Data', 'Element', 'Filter', 'Legend']), // Remember that the format matter: ["<panel1>", "<panel2>"]
        cameraPosition: undefined,
        SHADOW_MODE: false,
        LOW_PERFORMANCE: false,
    };

    data = {
        ACTIVE_GEODATA_ID: 0,
        ACTIVE_SIMULATION_SET_ID: 0,
        ACTIVE_LAYER_ID: 0,
        OLD_ACTIVE_LAYER_ID: 0,
        SHOW_ZERO_VALUE_POINTS: true,
        MULTIMODE_TRANSPARENT: false,

        IS_MIXER_ACTIVE: false,
        IS_MIXER_AND_LOGIC: false,
        MIXER_ELEMENTS: [],
    };

    state = {
        isCacheEnabled: true,
    };

    context = {
        // Context:
        SHOW_GROUND: true,
        SHOW_BUILDINGS: true,
        SHOW_TREES: true,
        SHOW_ROADS: true,
        SHOW_TARGETS: true,
        SHOW_TARGETS_BORDER: false,
        SHOW_WIND_STATIONS: false,
        SHOW_CUSTOM_OBSTRUCTION: true,
        SHOW_PROJECT_SKETCH: true,

        SHOW_GRID: true,
        SHOW_GRID_BOX: false,
        SHOW_GRID_CENTER: false,
        SHOW_ANALYSIS_BORDER: true,
        SHOW_LEGAL_BORDER: true,
        SHOW_BUILDABLE_BORDER: false,
        SHOW_GRID_AXES: false,
    };

    ambient = {
        WIREFRAME: false,

        CAMERA_NORM: false,
        CAMERA_FAR: false,
        CAMERA_NEAR: false,
        CAMERA_TOP: false,
        CAMERA_FRONT: false,
        CAMERA_POV: false,
        CAMERA_2D: false,

        CAMERA_VERTICAL: true,
        CAMERA_HORIZONTAL: false,
        CUSTOM_CAMERAS: [],
        CAMERA_DIRECTION: false,
        CAMERA_RESTORE: false,
        CAMERA_WASD_SPEED: 1,

        FAR_PLANE: 40000,
    };

    operator = {
        LOAD_RAYS: false,
        LOAD_INTS: false,
    };

    multiBuilding = {
        ACTIVE: false,
        numBuildings: 1,
        minLength: 6.0,
        buildingLength: 6.0,
        buildingDistance: 1.0,
        shadow: false,
        automaticSensorPoint: true,
    };

    columnSettings = {
        UI: false,
        VIDEO: false,
    };

    editGeoData = {
        DRAW_MODE: false,
    };

    newDS = {
        idIncrementalCounter: 0,
        selectedBuildingID: -1,
        buildings: [], // A single buildings stores: ID, points, visible
        selectedLayerID: -1,
        selectedSimSetID: -1,
        selectedGeodataID: -1,
    };

    solarAnalysis = {
        currentMatchingDemand: null,
        isMatchingDemand: false,
        isMatchingHeatingDemand: false,
        matchingDemandQueue: [],
        hasFinished: false,
    };
}
